import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmblaCarousel } from '@/components/EmblaCarousel';
import { MediaType } from '@/helpers/enums';
import { useResponsive } from '@/modules/client';
import { cx } from '@/modules/common/helpers/classNames';
import { useGetCommonStore } from '@/store';
import type { ICardHudConfig } from '../../helpers';
import { LevelCard } from './components/LevelCard';
import { ProgressBar } from './components/ProgressBar';
import { type ILevelItem, LevelState } from './helpers';
import { ScMissionLevels } from './styled';

interface IMissionLevelsProps {
  levels: ILevelItem[];
  defaultLevelIndex: number;
  namespace: string;
  cardHud: ICardHudConfig;
  onClaim: (id: string) => void;
  onLevelChange?: (index: number) => void;
  progressBarVariant?: 'pill';
  mobileMediaType?: MediaType;
  mobileBreakpoint?: MediaType;
}

export const MissionLevels: FC<IMissionLevelsProps> = ({
  levels,
  defaultLevelIndex,
  namespace,
  cardHud,
  onClaim,
  onLevelChange,
  progressBarVariant,
  mobileBreakpoint = MediaType.MOBILE,
}) => {
  const { t } = useTranslation(namespace);
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const { currentMediaType } = useResponsive();
  const isMobile = currentMediaType <= mobileBreakpoint;

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedLevelIndex = selectedId
    ? levels.findIndex((level) => level.id === selectedId)
    : -1;
  const currentLevelIndex =
    selectedLevelIndex !== -1 ? selectedLevelIndex : defaultLevelIndex;
  const currentLevel = levels[currentLevelIndex];
  // percent === 100 also covers levels forced back to Locked while
  // isBonusDataPending, so the highlight doesn't drop during that load.
  const hasAnyProgress = levels.some(
    (level) => level.state !== LevelState.Locked || level.percent === 100
  );
  useEffect(() => {
    onLevelChange?.(currentLevelIndex);
  }, [currentLevelIndex, onLevelChange]);

  const handleSlideChange = useCallback(
    (lastItemIndex: number) => {
      for (let index = lastItemIndex; index >= 0; index--) {
        const level = levels[index];
        if (level && level.state !== LevelState.Locked) {
          setSelectedId(level.id);
          return;
        }
      }
    },
    [levels]
  );

  const slides = useMemo(
    () =>
      levels.map((level, index) => ({
        ...level,
        namespace,
        cardHud,
        isSelected:
          isAuthenticated &&
          (selectedLevelIndex !== -1 || hasAnyProgress) &&
          level.id === currentLevel?.id,
        isLast: index === levels.length - 1,
        onClaim,
        onSelect: setSelectedId,
      })),
    [
      levels,
      namespace,
      cardHud,
      currentLevel,
      onClaim,
      isAuthenticated,
      hasAnyProgress,
      selectedLevelIndex,
    ]
  );

  return (
    <ScMissionLevels className={cx(isAuthenticated && 'auth')}>
      <div className="bgFrame" />

      <p className="levelsTitle">{t('missionLevels.title')}</p>

      {isAuthenticated ? (
        <ProgressBar
          level={currentLevel}
          levelNumber={currentLevelIndex + 1}
          namespace={namespace}
          variant={progressBarVariant}
        />
      ) : (
        <div className="guestMeter">
          <div className="divider" />
          <p className="guestTitle">{t('missionLevels.guestTitle')}</p>
          <p className="guestCaption">{t('missionLevels.guestCaption')}</p>
        </div>
      )}

      <EmblaCarousel
        arrows
        compactPagination
        groupSlides={!isMobile}
        onSlideChange={handleSlideChange}
        options={{
          align: 'center',
          containScroll: 'trimSnaps',
          startIndex: defaultLevelIndex,
        }}
        pagination
        slide={LevelCard}
        slides={slides}
        slidesPerView={isMobile ? 1 : 3}
        spaceBetween={64}
      />
    </ScMissionLevels>
  );
};
